import type { AttachmentLink, ProjectRecord, TaskComment } from "./types";

/**
 * RAID log for the ERP sample: every kind in the 「새 기록」 menu, every status of each kind,
 * with a superseded decision chain, a risk that turned into an issue, threaded comments and attachments.
 * Task ids refer to the ERP sample schedule and execution tasks; elsewhere the links are pruned on import.
 */

const PREVIEW_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="160" viewBox="0 0 320 160"><rect width="320" height="160" fill="#f3f4f6"/><rect x="24" y="28" width="120" height="104" rx="6" fill="#dbeafe" stroke="#60a5fa"/><rect x="176" y="28" width="120" height="104" rx="6" fill="#dcfce7" stroke="#4ade80"/><path d="M144 80h32" stroke="#6b7280" stroke-width="2" marker-end="url(#a)"/><defs><marker id="a" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0 0l8 4-8 4z" fill="#6b7280"/></marker></defs><text x="84" y="84" font-size="14" text-anchor="middle" fill="#1e3a8a">ERP</text><text x="236" y="84" font-size="14" text-anchor="middle" fill="#14532d">사내 IdP</text></svg>`,
  );

function link(id: string, title: string, url: string, at: string, addedBy: string): AttachmentLink {
  return { id, title, url, addedAt: at, kind: "link", addedBy };
}

function file(id: string, title: string, size: number, at: string, addedBy: string, mimeType = "application/pdf"): AttachmentLink {
  return { id, title, url: `https://example.com/files/${encodeURIComponent(title)}`, addedAt: at, kind: "file", size, mimeType, addedBy };
}

function comment(id: string, author: string, at: string, body: string, extra?: Partial<TaskComment>): TaskComment {
  return { id, author, at, body, ...extra };
}

export function sampleErpRecords(): ProjectRecord[] {
  const base = (id: string, kind: ProjectRecord["kind"], seq: number, title: string, at: string, extra: Partial<ProjectRecord>): ProjectRecord => ({
    id,
    kind,
    seq,
    title,
    status: "open",
    assignees: [],
    impacts: [],
    sections: {},
    linkedTaskIds: [],
    linkedExecutionTaskIds: [],
    linkedRecordIds: [],
    attachments: [],
    comments: [],
    activity: [{ id: `${id}_ac0`, at, actor: "김PM", message: "생성" }],
    createdAt: at,
    updatedAt: at,
    ...extra,
  });

  return [
    /* ------------------------------------------------------------------ 결정 */
    base("r_dec_login", "decision", 1, "로그인 방식: 자체 ID/PW 인증", "2026-09-10T01:00:00.000Z", {
      status: "superseded",
      source: "internal",
      assignees: ["이아키"],
      due: "2026-09-15",
      closedAt: "2026-10-08T05:00:00.000Z",
      updatedAt: "2026-10-08T05:00:00.000Z",
      sections: {
        context: "ERP 사용자 약 400명. 초기 제안서에는 자체 계정 관리로 작성됨.",
        decision: "ERP 자체 사용자 테이블과 ID/PW 로그인을 사용한다.",
        alternatives: "사내 SSO 연동 — 고객사 AD 정보 확인 전이라 보류.",
      },
      linkedTaskIds: ["t3-1"],
      activity: [
        { id: "r_dec_login_ac2", at: "2026-10-08T05:00:00.000Z", actor: "김PM", message: "DEC-2로 대체됨" },
        { id: "r_dec_login_ac1", at: "2026-09-12T02:00:00.000Z", actor: "김PM", message: "상태 → 확정" },
        { id: "r_dec_login_ac0", at: "2026-09-10T01:00:00.000Z", actor: "김PM", message: "생성" },
      ],
    }),
    base("r_dec_sso", "decision", 2, "로그인 방식: 사내 SSO(SAML 2.0) 연동", "2026-10-06T02:00:00.000Z", {
      status: "accepted",
      source: "client",
      assignees: ["이아키", "박엔진"],
      due: "2026-10-08",
      impacts: ["schedule", "scope"],
      supersedes: "r_dec_login",
      updatedAt: "2026-10-08T05:00:00.000Z",
      sections: {
        context: "고객사 보안팀이 모든 신규 시스템은 사내 SSO를 거치도록 요구 (보안지침 3.1).",
        decision: "SAML 2.0으로 사내 IdP와 연동한다. ERP 자체 비밀번호는 두지 않는다.",
        alternatives: "OIDC — 고객사 IdP가 SAML만 지원해 제외.",
        consequences: "인터페이스 설계에 IdP 연동 항목 추가. 로컬 관리자 계정 1개는 비상용으로 유지.",
      },
      linkedTaskIds: ["t3-1", "t3-4"],
      linkedRecordIds: ["r_pol_session"],
      attachments: [
        { id: "r_dec_sso_img", title: "SSO 연동 구성도.svg", url: PREVIEW_IMAGE, addedAt: "2026-10-06T03:00:00.000Z", kind: "image", mimeType: "image/svg+xml", size: 1180, addedBy: "이아키" },
        file("r_dec_sso_f1", "SSO 연동 검토 회의록_1006.pdf", 248_000, "2026-10-06T08:00:00.000Z", "이아키"),
        link("r_dec_sso_l1", "SAML 2.0 기술 개요", "https://docs.oasis-open.org/security/saml/Post2.0/sstc-saml-tech-overview-2.0.html", "2026-10-06T03:10:00.000Z", "이아키"),
      ],
      comments: [
        comment("r_dec_sso_cm1", "이아키", "2026-10-07T04:00:00.000Z", "IdP 메타데이터는 고객사 보안팀에서 10/15까지 전달 예정입니다."),
        comment("r_dec_sso_cm2", "김PM", "2026-10-07T05:20:00.000Z", "비상용 로컬 관리자 계정은 보안팀 승인 받았나요?", { parentId: "r_dec_sso_cm1" }),
        comment("r_dec_sso_cm3", "이아키", "2026-10-07T06:05:00.000Z", "네, 10/7 메일로 승인 받았습니다. 계정은 1개, 분기별 비밀번호 교체 조건입니다.", { parentId: "r_dec_sso_cm1" }),
      ],
      activity: [
        { id: "r_dec_sso_ac2", at: "2026-10-08T05:00:00.000Z", actor: "김PM", message: "상태 → 확정" },
        { id: "r_dec_sso_ac1", at: "2026-10-06T03:00:00.000Z", actor: "이아키", message: "첨부 추가: SSO 연동 구성도.svg" },
        { id: "r_dec_sso_ac0", at: "2026-10-06T02:00:00.000Z", actor: "김PM", message: "생성" },
      ],
    }),
    base("r_dec_report", "decision", 3, "경영 리포트 도구: ERP 내장 리포트 vs 외부 BI", "2026-10-15T02:00:00.000Z", {
      status: "proposed",
      source: "internal",
      assignees: ["이아키"],
      due: "2026-10-28",
      impacts: ["cost", "scope"],
      sections: {
        context: "재무팀이 월 마감 리포트 12종을 요구. ERP 기본 리포트로는 피벗·드릴다운이 부족함.",
        decision: "(검토 중) 기본 12종은 ERP 내장 리포트로, 임원 대시보드만 외부 BI로 분리하는 안을 제안.",
        alternatives: "전부 외부 BI — 라이선스 연 1,800만 원 추가. 전부 내장 — 대시보드 요구 미충족.",
      },
      linkedTaskIds: ["t2-4", "t4-4"],
      comments: [comment("r_dec_report_cm1", "최기획", "2026-10-16T01:30:00.000Z", "재무팀장님이 10/24 회의에서 결정하시겠다고 합니다.")],
    }),
    base("r_dec_mobile", "decision", 4, "현장 입력용 모바일 앱 별도 개발", "2026-09-25T04:00:00.000Z", {
      status: "rejected",
      source: "client",
      assignees: ["김PM"],
      closedAt: "2026-09-30T02:00:00.000Z",
      sections: {
        context: "생산 현장에서 태블릿 입력 요구.",
        decision: "별도 앱은 만들지 않고 반응형 웹 화면으로 대응한다.",
        alternatives: "네이티브 앱 — 공수 2.5M/M 추가, 범위 외.",
        consequences: "화면 설계에 태블릿 해상도(1280×800) 기준 추가.",
      },
      linkedTaskIds: ["t3-3"],
    }),

    /* ------------------------------------------------------------------ 정책 */
    base("r_pol_session", "policy", 1, "세션 및 계정 잠금 정책", "2026-10-07T01:00:00.000Z", {
      status: "accepted",
      source: "client",
      assignees: ["김PM"],
      due: "2026-11-01",
      sections: {
        statement: "30분 동안 입력이 없으면 세션을 만료한다. 로그인 5회 연속 실패 시 계정을 30분 잠근다.",
        applicability: "ERP 웹 전체 화면. 배치·인터페이스 계정은 제외.",
        exceptions: "생산 현장 키오스크는 세션 만료 8시간 — 생산팀장 승인 필요.",
        context: "고객사 정보보안 지침 v2.3 · 3.2 세션 관리",
      },
      linkedTaskIds: ["t3-3"],
      attachments: [file("r_pol_session_f1", "고객사 정보보안 지침 v2.3.pdf", 1_420_000, "2026-10-07T01:00:00.000Z", "김PM")],
      comments: [comment("r_pol_session_cm1", "정프론트", "2026-10-09T07:00:00.000Z", "만료 5분 전 경고 모달을 띄워도 될까요? 지침에는 언급이 없습니다.")],
    }),
    base("r_pol_privacy", "policy", 2, "개인정보 화면 마스킹 정책", "2026-10-09T02:00:00.000Z", {
      status: "accepted",
      source: "regulation",
      assignees: ["정프론트", "오품질"],
      due: "2026-11-15",
      sections: {
        statement: "주민번호 뒷자리·계좌번호·연락처 가운데 4자리는 목록·조회 화면에서 * 로 가린다. 원본 조회는 권한자만, 조회 이력을 남긴다.",
        applicability: "인사·급여·거래처 화면, 엑셀 다운로드 포함.",
        exceptions: "급여 담당자의 급여 명세 화면은 계좌번호 전체 표시.",
        context: "개인정보 보호법 제29조, 고객사 개인정보 처리방침 4조",
      },
      linkedTaskIds: ["t3-3", "t4-4", "t5-1"],
      attachments: [link("r_pol_privacy_l1", "개인정보 보호법 제29조", "https://www.law.go.kr/법령/개인정보보호법", "2026-10-09T02:00:00.000Z", "정프론트")],
    }),
    base("r_pol_retention", "policy", 4, "로그 보관 기간 3년", "2026-10-17T03:00:00.000Z", {
      status: "accepted",
      source: "client",
      assignees: ["강인프라"],
      due: "2026-12-01",
      supersedes: "r_pol_retention_old",
      updatedAt: "2026-10-21T02:00:00.000Z",
      sections: {
        statement: "접속·권한 변경 로그를 3년간 보관한다.",
        applicability: "ERP WAS·DB 감사 로그.",
        context: "고객사 내부 감사 권고 (2026-10-10)",
      },
      linkedTaskIds: ["t4-1"],
      comments: [
        comment("r_pol_retention_cm1", "강인프라", "2026-10-17T05:00:00.000Z", "3년 보관 시 로그 스토리지 약 2TB 추가 필요. 비용 확인 후 확정 요청드립니다."),
        comment("r_pol_retention_cm2", "김PM", "2026-10-21T02:00:00.000Z", "고객사가 스토리지 비용 부담하기로 해서 확정했습니다.", { parentId: "r_pol_retention_cm1" }),
      ],
    }),
    base("r_pol_retention_old", "policy", 3, "로그 보관 기간 1년", "2026-09-05T03:00:00.000Z", {
      status: "superseded",
      source: "internal",
      assignees: ["강인프라"],
      closedAt: "2026-10-21T02:00:00.000Z",
      updatedAt: "2026-10-21T02:00:00.000Z",
      sections: {
        statement: "접속 로그를 1년간 보관한다.",
        applicability: "ERP WAS 접속 로그.",
      },
      activity: [
        { id: "r_pol_retention_old_ac1", at: "2026-10-21T02:00:00.000Z", actor: "김PM", message: "POL-4로 대체됨" },
        { id: "r_pol_retention_old_ac0", at: "2026-09-05T03:00:00.000Z", actor: "김PM", message: "생성" },
      ],
    }),
    base("r_pol_vendor_account", "policy", 5, "협력사 계정 발급 및 만료 정책", "2026-10-20T01:00:00.000Z", {
      status: "proposed",
      source: "internal",
      assignees: ["김PM", "강인프라"],
      due: "2026-11-15",
      sections: {
        statement: "협력사 인력 계정은 계약 종료일에 자동 만료한다. 발급은 PM 승인 후 인프라 담당이 처리한다.",
        applicability: "개발·테스트·운영 서버, ERP 관리자 화면.",
        exceptions: "하자보수 기간 중 유지보수 인력은 30일 단위 연장.",
      },
      linkedTaskIds: ["t4-1", "t6-5"],
    }),
    base("r_pol_usb", "policy", 6, "프로젝트룸 USB 저장장치 허용", "2026-09-01T00:00:00.000Z", {
      status: "deprecated",
      source: "client",
      assignees: ["강인프라"],
      closedAt: "2026-09-20T00:00:00.000Z",
      sections: {
        statement: "프로젝트룸 PC에서 암호화 USB 사용을 허용한다.",
        context: "고객사 보안팀 요청으로 9/20부터 전면 금지 (파일 전송은 사내 NAS로 일원화).",
      },
    }),

    /* ------------------------------------------------------------------ 이슈 */
    base("r_iss_ad", "issue", 1, "고객사 AD 계정 속성 정보 제공 지연", "2026-10-14T00:30:00.000Z", {
      status: "inProgress",
      severity: "high",
      source: "client",
      assignees: ["김PM"],
      due: "2026-10-22",
      impacts: ["schedule"],
      sections: {
        summary: "SSO 연동 설계에 필요한 AD 속성(부서코드·사번) 매핑표를 10/10까지 받기로 했으나 아직 미수령.",
        cause: "고객사 인프라팀 담당자 교체로 요청이 누락됨.",
      },
      linkedTaskIds: ["t3-4"],
      linkedExecutionTaskIds: ["x_mes_if"],
      linkedRecordIds: ["r_dec_sso"],
      attachments: [file("r_iss_ad_f1", "AD 속성 요청 공문_1015.pdf", 96_000, "2026-10-15T06:00:00.000Z", "김PM")],
      comments: [
        comment("r_iss_ad_cm1", "김PM", "2026-10-15T06:00:00.000Z", "고객사 PM에게 공식 요청 메일 재발송. 10/20 회신 약속 받았습니다.", {
          attachments: [file("r_iss_ad_cm1_f", "요청 메일 캡처.pdf", 54_000, "2026-10-15T06:00:00.000Z", "김PM")],
        }),
        comment("r_iss_ad_cm2", "박엔진", "2026-10-15T07:10:00.000Z", "우선 샘플 계정 10개로 인터페이스 목업 진행하겠습니다.", { parentId: "r_iss_ad_cm1" }),
        comment("r_iss_ad_cm3", "김PM", "2026-10-20T02:00:00.000Z", "회신 다시 지연. 10/22까지 안 오면 주간 보고에서 일정 영향 보고 예정."),
      ],
    }),
    base("r_iss_mes", "issue", 2, "MES 인터페이스 사양 버전 불일치", "2026-10-18T01:00:00.000Z", {
      status: "open",
      severity: "medium",
      source: "vendor",
      assignees: ["박엔진"],
      due: "2026-10-29",
      impacts: ["schedule", "quality"],
      sections: {
        summary: "MES 업체가 보낸 인터페이스 사양서(v1.3)와 운영 중인 MES(v1.1)의 전문 필드가 다름.",
        cause: "RSK-3 에서 우려했던 협력사 사양 변경이 실제로 발생.",
      },
      linkedTaskIds: ["t3-4", "t4-5"],
      linkedExecutionTaskIds: ["x_mes_if"],
      linkedRecordIds: ["r_rsk_vendor"],
    }),
    base("r_iss_interview", "issue", 3, "현업 인터뷰 녹음 후반부 누락", "2026-09-22T05:30:00.000Z", {
      status: "resolved",
      severity: "low",
      source: "internal",
      assignees: ["최기획"],
      due: "2026-09-26",
      closedAt: "2026-09-25T08:00:00.000Z",
      sections: {
        summary: "구매팀 인터뷰 녹음기 오류로 후반 30분이 저장되지 않음.",
        resolution: "구매팀장과 30분 보충 인터뷰(9/25) 진행, 회의록 보완 완료.",
      },
      linkedTaskIds: ["t2-1"],
      linkedExecutionTaskIds: ["x_interview_bug"],
    }),
    base("r_iss_room", "issue", 4, "프로젝트룸 네트워크 방화벽 미개방", "2026-09-02T00:00:00.000Z", {
      status: "closed",
      severity: "medium",
      source: "client",
      assignees: ["강인프라"],
      due: "2026-09-04",
      closedAt: "2026-09-04T09:00:00.000Z",
      sections: {
        summary: "개발 서버 접속 포트(22, 8080) 미개방으로 착수 첫 주 원격 작업 불가.",
        resolution: "고객사 보안팀 방화벽 신청서 승인 후 9/4 개방.",
      },
      linkedTaskIds: ["t4-1"],
    }),

    /* ------------------------------------------------------------------ 리스크 */
    base("r_rsk_migration", "risk", 1, "레거시 데이터 품질 불량으로 이관 지연 가능", "2026-10-02T03:00:00.000Z", {
      status: "mitigating",
      severity: "medium",
      source: "internal",
      assignees: ["송DB"],
      due: "2026-12-15",
      impacts: ["schedule", "quality"],
      sections: {
        summary: "품목 마스터 중복·단위 불일치가 많으면 리허설 이관에서 오류가 누적되어 본 이관 일정이 밀릴 수 있음.",
        mitigation: "설계 단계에서 표본 1,000건 정합성 점검. 정제 규칙을 이관 프로그램에 포함.",
      },
      linkedTaskIds: ["t4-7", "t6-2"],
      attachments: [file("r_rsk_migration_f1", "품목마스터 표본점검 결과.xlsx", 312_000, "2026-10-12T04:00:00.000Z", "송DB", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")],
      comments: [comment("r_rsk_migration_cm1", "송DB", "2026-10-12T04:00:00.000Z", "표본 1,000건 중 중복 87건(8.7%), 단위 불일치 23건. 정제 규칙 초안 첨부합니다.")],
    }),
    base("r_rsk_keyman", "risk", 2, "핵심 개발자 1인 의존 (생산 모듈)", "2026-10-10T02:00:00.000Z", {
      status: "identified",
      severity: "high",
      source: "internal",
      assignees: ["김PM"],
      due: "2026-11-05",
      impacts: ["schedule", "cost"],
      sections: {
        summary: "생산 모듈 로직을 박엔진 1인만 알고 있음. 이탈·장기 휴가 시 개발 2~3주 지연.",
        mitigation: "주 1회 코드 리뷰로 지식 공유, 11월부터 보조 개발자 1명 투입 검토.",
      },
      linkedTaskIds: ["t4-2"],
    }),
    base("r_rsk_vendor", "risk", 3, "MES 협력사 사양 변경 가능성", "2026-09-20T06:00:00.000Z", {
      status: "occurred",
      severity: "medium",
      source: "vendor",
      assignees: ["박엔진"],
      impacts: ["schedule"],
      sections: {
        summary: "MES 업체가 연말 버전 업그레이드를 예고. 인터페이스 사양이 바뀌면 재작업 발생.",
        mitigation: "사양 동결일을 계약서에 명시 요청. 인터페이스를 어댑터 계층으로 분리.",
      },
      linkedTaskIds: ["t3-4"],
      linkedRecordIds: ["r_iss_mes"],
      activity: [
        { id: "r_rsk_vendor_ac1", at: "2026-10-18T01:00:00.000Z", actor: "박엔진", message: "ISS-2로 이슈 등록" },
        { id: "r_rsk_vendor_ac0", at: "2026-09-20T06:00:00.000Z", actor: "김PM", message: "생성" },
      ],
    }),
    base("r_rsk_license", "risk", 4, "DB 라이선스 도입 지연", "2026-09-08T01:00:00.000Z", {
      status: "closed",
      severity: "low",
      source: "vendor",
      assignees: ["강인프라"],
      closedAt: "2026-09-19T03:00:00.000Z",
      sections: {
        summary: "Oracle 라이선스 구매 결재가 늦어지면 개발 환경 구축이 밀릴 수 있음.",
        mitigation: "임시 개발 라이선스로 선구축. 9/19 정식 라이선스 입고로 종결.",
      },
      linkedTaskIds: ["t4-1"],
    }),

    /* ------------------------------------------------------------------ 변경 요청 */
    base("r_chg_approval", "change", 1, "구매 결재선 3단계 → 4단계 변경", "2026-10-16T05:00:00.000Z", {
      status: "reviewing",
      severity: "medium",
      source: "client",
      assignees: ["최기획", "박엔진"],
      due: "2026-10-30",
      impacts: ["scope", "schedule"],
      sections: {
        summary: "1천만 원 이상 구매 요청에 재무팀장 결재 단계를 추가.",
        reason: "고객사 내부 감사 지적 사항 반영.",
        impactAnalysis: "구매 모듈 결재 화면·알림 수정 약 3일. 요구사항 정의서 개정 필요.",
      },
      linkedTaskIds: ["t2-5", "t4-3"],
      linkedExecutionTaskIds: ["x_req_sign"],
      comments: [comment("r_chg_approval_cm1", "최기획", "2026-10-17T02:00:00.000Z", "변경 영향 3일은 요구사항 확정 전이라 추가 비용 없이 흡수 가능할 것 같습니다.")],
    }),
    base("r_chg_label", "change", 2, "바코드 라벨 양식 변경 (QR 추가)", "2026-10-19T03:00:00.000Z", {
      status: "requested",
      severity: "low",
      source: "client",
      assignees: ["정프론트"],
      due: "2026-11-10",
      impacts: ["scope"],
      sections: {
        summary: "출고 라벨에 QR 코드와 로트번호 추가.",
        reason: "물류 협력사 스캐너 교체.",
      },
      linkedTaskIds: ["t4-3"],
    }),
    base("r_chg_cost", "change", 3, "원가 계산 방식 표준원가 → 실제원가", "2026-10-03T02:00:00.000Z", {
      status: "approved",
      severity: "high",
      source: "client",
      assignees: ["송DB"],
      due: "2026-11-20",
      impacts: ["scope", "schedule", "cost"],
      sections: {
        summary: "재무·회계 모듈의 원가 계산을 실제원가 기준으로 변경.",
        reason: "고객사 회계 정책 변경 (2027 회계연도부터).",
        impactAnalysis: "재무 모듈 개발 +8일, 추가 공수 0.4M/M. 변경 계약 10/10 체결.",
      },
      linkedTaskIds: ["t4-4"],
      attachments: [file("r_chg_cost_f1", "변경계약서_원가계산.pdf", 530_000, "2026-10-10T05:00:00.000Z", "송DB")],
    }),
    base("r_chg_lang", "change", 4, "다국어(영문) 화면 추가", "2026-09-28T02:00:00.000Z", {
      status: "rejected",
      severity: "medium",
      source: "client",
      assignees: ["김PM"],
      closedAt: "2026-10-01T06:00:00.000Z",
      sections: {
        summary: "해외 법인 사용자를 위한 영문 화면.",
        reason: "베트남 법인 1차 오픈 검토.",
        impactAnalysis: "전 화면 다국어 처리 +1.2M/M. 이번 범위에서 제외하고 2차 사업으로 분리.",
      },
    }),
    base("r_chg_menu", "change", 5, "메인 메뉴 순서 부서별 커스터마이즈", "2026-09-15T02:00:00.000Z", {
      status: "applied",
      severity: "low",
      source: "client",
      assignees: ["정프론트"],
      due: "2026-09-30",
      closedAt: "2026-09-29T08:00:00.000Z",
      impacts: ["scope"],
      sections: {
        summary: "부서별로 자주 쓰는 메뉴를 상단에 고정.",
        reason: "현업 인터뷰 공통 요청.",
        impactAnalysis: "메뉴 설정 테이블 1개 추가, 0.5일.",
      },
      linkedTaskIds: ["t3-3"],
    }),
    /* ------------------------------------------------------------------ 가정 */
    base("r_asm_users", "assumption", 1, "ERP 사용자는 약 400명이다", "2026-09-08T01:00:00.000Z", {
      status: "proposed",
      source: "client",
      assignees: ["정발주"],
      due: "2026-10-15",
      sections: {
        statement: "본사와 공장 사용자를 합쳐 400명으로 라이선스와 교육을 산정한다.",
        context: "착수 미팅에서 고객 PM이 말한 대략의 인원. 인사 명부는 아직 받지 못했다.",
        impactAnalysis: "600명을 넘으면 교육 일정과 라이선스 비용이 다시 열린다.",
      },
    }),
    base("r_asm_sso", "assumption", 2, "사내 IdP는 SAML 2.0만 지원한다", "2026-10-06T03:00:00.000Z", {
      status: "accepted",
      source: "client",
      assignees: ["이아키"],
      due: "2026-10-08",
      sections: {
        statement: "고객사 IdP는 SAML 2.0만 제공하고 OIDC는 이번 범위에 없다.",
        context: "보안팀 회신 (2026-10-06).",
      },
    }),
    base("r_asm_holiday", "assumption", 3, "설 연휴에 공장은 멈춘다", "2026-09-20T02:00:00.000Z", {
      status: "rejected",
      source: "internal",
      assignees: ["김PM"],
      closedAt: "2026-10-02T04:00:00.000Z",
      sections: {
        statement: "설 연휴 전 주는 현업 인터뷰를 잡지 않는다.",
        impactAnalysis: "공장 가동 일정이 확정되며 가정이 깨졌다.",
      },
    }),
    base("r_asm_vendor", "assumption", 4, "장비 업체 인터페이스는 확정본이다", "2026-09-12T02:00:00.000Z", {
      status: "closed",
      source: "vendor",
      assignees: ["강인프라"],
      closedAt: "2026-09-30T06:00:00.000Z",
      sections: {
        statement: "협력사 인터페이스 명세 1.0을 변경 없이 쓴다.",
        context: "명세가 2.0으로 바뀌어 가정을 닫았다.",
      },
    }),
  ];
}
